import {HomeHowItWorksItem} from '#/components/home-content/how-it-works';
import {ReactComponent as IconDiscord} from '#/components/icons/ico-discord.svg';
import {LandThumbnail} from '#/components/lands/land-thumbnail';
import {LoginGroup} from '#/components/login/login-group';
import {useLands} from '#/hooks/query/use-lands';
import {useScrollEdges} from '#/hooks/use-is-scrolling';
import {Page} from '#/pages/page';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {FunctionComponent} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useMedia} from 'react-use';

export const LandingPage: FunctionComponent<DefaultChildren> = () => {
  const {t} = useTranslation('translation');

  const isMinWidth = {
    sm: useMedia('(min-width: 640px)'),
    xl: useMedia('(min-width: 1280px)'),
  };

  return (
    <Page title={t('pages.landing.htmlTitle')}>
      <div className='flex min-h-full flex-col overflow-y-scroll'>
        <Header />

        <div className='grow flex flex-col items-start xs:items-center justify-center mt-10 w-full bg-white gap-8 md:hidden overflow-x-visible'>
          <WelcomeToShareland className='text-5xl px-5 xs:text-center' />
          <LandsGrid className='w-full px-5 h-32 gap-4 grid grid-rows-1 auto-cols-[220px] grid-flow-col-dense overflow-x-scroll' />
          <Explanation className='px-5 xs:max-w-[400px] xs:text-center' />
          <LoginGroup className='px-5 xs:max-w-[400px]' />
          <Questions className='px-5 xs:max-w-[400px] xs:text-center' />
        </div>

        <div className='grow items-center w-full bg-white px-5 max-w-screen-2xl mx-auto hidden md:flex'>
          <div className='w-full h-full grid content-center grid-rows-landingPage grid-cols-[auto,340px] gap-x-8 lg:grid-cols-[auto,420px] lg:gap-x-16'>
            {!isMinWidth.xl ? (
              <LandsGrid numberOfElements={4} className='gap-5 grid grid-rows-2 grid-cols-2' />
            ) : (
              <LandsGrid numberOfElements={6} className='gap-5 grid grid-rows-2 grid-cols-3' />
            )}
            <div className={'text-left space-y-8'}>
              <WelcomeToShareland className='text-6xl' />
              <Explanation />
              <LoginGroup className='max-w-[360px]' />
              <Questions />
            </div>
          </div>
        </div>

        {!isMinWidth.sm ? <HowItWorksVertical /> : <HowItWorksHorizontal />}
      </div>
    </Page>
  );
};

const Header: FunctionComponent = () => {
  return (
    <header className={'h-header flex justify-between items-center w-full bg-white py-3 px-5 max-w-screen-2xl mx-auto'}>
      <img
        className='mb-[2px] pointer-events-auto w-28'
        src='https://uploads-ssl.webflow.com/625f2bde0792a2bcb3c92a01/65bac5bf687db05e67292ff4_Frame%208834.png'
        alt='shareland-logo-white'
      />
    </header>
  );
};

const WelcomeToShareland: FunctionComponent<ClassName> = ({className}) => {
  const {t} = useTranslation();
  return <span className={`block font-bold ${className}`}>{t('pages.landing.explanation.welcome')}</span>;
};

const Explanation: FunctionComponent<ClassName> = ({className}) => {
  const {t} = useTranslation();
  return (
    <span className={`block text-base sm:text-lg text-gray-400 ${className}`}>
      {t('pages.landing.explanation.loginAction')}
    </span>
  );
};

const Questions: FunctionComponent<ClassName> = ({className}) => (
  <span className={`block text-base sm:text-lg text-gray-400 ${className}`}>
    <Trans
      i18nKey={'pages.landing.explanation.questions'}
      components={{
        CTADiscord: <DiscordLink />,
      }}
    />
  </span>
);

export const DiscordLink: FunctionComponent<DefaultChildren> = ({children}) => (
  <a
    className='inline-flex gap-1 items-center text-deepblue'
    href='https://discord.gg/5njaDH5y39'
    target='_blank'
    rel='noreferrer'
  >
    {children}
    <IconDiscord className='w-5 h-5 fill-deepblue stroke-deepblue' />
  </a>
);

const HowItWorksVertical: FunctionComponent = () => {
  return (
    <div className='relative w-full flex justify-start items-center bg-gray-200 p-5 mt-10'>
      <div className='w-full space-y-4 divide-y divide-gray-400'>
        <HomeHowItWorksItem i18nKey='explore' className='w-full' />
        <HomeHowItWorksItem i18nKey='transact' className='w-full pt-3' />
        <HomeHowItWorksItem i18nKey='monitor' className='w-full pt-3' />
      </div>
    </div>
  );
};

const HowItWorksHorizontal: FunctionComponent = () => {
  const {scrollEdges, onScroll} = useScrollEdges();

  return (
    <div className='relative w-full flex justify-start items-center bg-gray-200 py-10 px-0 mt-10 overflow-hidden'>
      <div
        onScroll={onScroll}
        className='w-full px-5 space-y-0 grid auto-rows-max auto-cols-max grid-flow-col-dense gap-x-10 overflow-x-scroll divide-x divide-gray-400 max-w-screen-2xl mx-auto'
      >
        <HomeHowItWorksItem i18nKey='explore' className='w-full' />
        <HomeHowItWorksItem i18nKey='transact' className='w-full pt-0 pl-10' />
        <HomeHowItWorksItem i18nKey='monitor' className='w-full pt-0 pl-10' />
      </div>

      {(scrollEdges === 'right' || scrollEdges === 'both') && (
        <div className='absolute top-0 left-0 h-full w-12 flex pointer-events-none'>
          <div className='h-full w-36 bg-gradient-to-r via-gray-200/70 from-gray-200' />
        </div>
      )}
      {(scrollEdges === 'left' || scrollEdges === 'both') && (
        <div className='absolute top-0 right-0 h-full flex pointer-events-none'>
          <div className='h-full w-36 bg-gradient-to-l via-gray-200/70 from-gray-200' />
        </div>
      )}
    </div>
  );
};

const LandsGrid: FunctionComponent<{numberOfElements?: number | 'max'} & ClassName> = ({
  numberOfElements = 'max',
  className,
}) => {
  const {t} = useTranslation('translation');

  const {data: lands = []} = useLands();

  const slicedLands = numberOfElements === 'max' ? lands : lands.slice(0, numberOfElements);

  return (
    <div className={className}>
      {slicedLands.map((land, index) => (
        <div key={index} className='relative bg-red-800 rounded-2xl overflow-hidden-clip-path group'>
          <LandThumbnail
            land={land}
            className={
              'absolute top-0 left-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500'
            }
          />

          <div className='absolute bottom-0 left-0 w-full bg-gradient-to-t from-black to-transparent px-5 pb-4 pt-8'>
            <div className='text-white font-bold text-left'>
              {t('components.locationGrid.tile.title', {
                landName: land.name,
              })}
            </div>
            <div className='text-white font-light text-left'>
              {t('components.locationGrid.tile.subtitle', {
                context: land.type === 'NEIGHBORHOOD' ? 'neighborhood' : 'city',
                landCity: land.city,
                landState: land.state,
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
